/* eslint no-console: 0 */

/**
 * Merge params & options and process cancelToken
 * @param params
 * @param options
 * @returns {{params: *, cancelToken}}
 */
const prepareOptions = (params, options) => ({
  params,
  ...options,
});

const fetchRequest = (url, options) => fetch(url, options)
  .then((response) => response.json())
  .catch((error) => {
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.log(error.response.data);
      console.log(error.response.status);
      console.log(error.response.headers);

      // Unauthorised Access
      if (error.response.status === 401) {
        // window.location.reload();
      }
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      console.log(error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.log('Error', error.message);
    }
    throw error;
  });

/**
 * Generic handling which will return directly data in case of success
 * and will log error in case of exception and then throws the error for custom handling
 *
 * @returns {*}
 */
const request = async (
  requestType,
  url,
  data,
  params,
  options,
) => {
  if (!url) {
    throw new Error('URL can not be empty');
  }

  // Check if session/token is valid, else refresh the token

  const urlParams = new URLSearchParams(params);

  const config = {
    method: requestType,
    body: data,
    ...prepareOptions(urlParams, options),
  };

  return fetchRequest(url, config);
};

const METHOD = {
  GET: 'get',
  PUT: 'put',
  POST: 'post',
  DELETE: 'delete',
};

export default {

  /**
   * Usage examples
   *
   * 1. Only URL
   *    HttpUtil.get('get/documents')
   *
   * 2. Promise (then & catch) handling
   *    HttpUtil.get('get/documents')
   *    .then((response) => {
   *      //Success Handling
   *      console.log(response);
   *    })
   *    .catch((error) => {
   *      //Custom Failure Handling
   *      console.log(error);
   *    });
   *
   *
   * 3. URL & params
   *    HttpUtil.get('get/document', {id: 1234});
   *
   *
   * 4. URL & Cancel Token Handler
   *    let cancelRequestMethod;
   *    let options = {
   *     cancel: (c) => {
   *        cancelRequestMethod = c;
   *      },
   *    }
   *    HttpUtil.get('/search/documents', null, options);
   *
   *    if (typeof cancelRequestMethod === 'function') {
   *       cancelRequestMethod();
   *    }
   *
   *   pass a method with key cancel in the options which will expect a cancellation method in
   *   parameter that can be used to cancel the request if needed.
   *   to cancel the request simply call the method cancelRequestMethod() as in the example
   *
   *
   * 5. URL, params and options
   *    HttpUtil.get('/search/documents', {id: 123}, options);
   *
   *    options can be from the e.g
   *    {
   *       headers: {'X-Requested-With': 'XMLHttpRequest'},
   *       timeout: 1000,
   *       withCredentials: false,
   *       responseType: 'json',
   *       responseEncoding: 'utf8',
   *       xsrfCookieName: 'XSRF-TOKEN'
   *       xsrfHeaderName: 'X-XSRF-TOKEN',
   *    }
   *    There are more options available, please refer below link for more
   *    @link https://github.com/axios/axios#request-config
   *
   *
   * @param url
   * @param params
   * @param options
   * @returns {*}
   */
  get(url, params, options) {
    return request(METHOD.GET, url, undefined, params, options);
  },

  /**
   * Usage examples
   *
   * 1. Only URL
   *    HttpUtil.post('search/documents');
   *
   * 2. URL with Data
   *    HttpUtil.post('filter/documents', { id: 4567 });
   *
   * 3. URL, Data and Params
   *    HttpUtil.post('filter/documents', { id: 4567 }, {type: 'document'});
   *
   * 4. URL, Data, Params and Options
   *    HttpUtil.post('filter/documents', { id: 4567 }, {type: 'document'}, options);
   *
   *
   * @param url
   * @param data
   * @param params
   * @param options
   * @returns {*}
   */
  post(url, data, params, options) {
    return request(METHOD.POST, url, data, params, options);
  },

  /**
   *
   * Usage examples
   *
   * 1. Only URL
   *    HttpUtil.put('search/documents');
   *
   * 2. URL with Data
   *    HttpUtil.put('filter/documents', { id: 4567 });
   *
   * 3. URL, Data and Params
   *    HttpUtil.put('filter/documents', { id: 4567 }, {type: 'document'});
   *
   * 4. URL, Data, Params and Options
   *    HttpUtil.put('filter/documents', { id: 4567 }, {type: 'document'}, options);
   *
   * @param url
   * @param data
   * @param params
   * @param options
   * @returns {*}
   */
  put(url, data, params, options) {
    return request(METHOD.PUT, url, data, params, options);
  },

  /**
   *
   * Usage examples
   *
   * 1. Only URL
   *    HttpUtil.delete('search/documents');
   *
   * 2. URL with Data
   *    HttpUtil.delete('filter/documents', { id: 4567 });
   *
   * 3. URL, Data and Params
   *    HttpUtil.delete('filter/documents', { id: 4567 }, {type: 'document'});
   *
   * 4. URL, Data, Params and Options
   *    HttpUtil.delete('filter/documents', { id: 4567 }, {type: 'document'}, options);
   *
   *
   * @param url
   * @param data
   * @param params
   * @param options
   * @returns {*}
   */
  delete(url, data, params, options) {
    return request(METHOD.DELETE, url, data, params, options);
  },
};
